import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import DirectionsCarFilledRoundedIcon from "@mui/icons-material/DirectionsCarFilledRounded";
import StatTile from "./StatTile";

/**
 * Honest replacement for the old CarModelViewer: no fake 3D model (it
 * rendered the same generic dune-buggy GLB for every make/model), just the
 * vehicle's identity plus a few real stats pulled from its own records.
 * Flat header section -- a heading followed by a row of stats separated by
 * hairline dividers, not a boxed card.
 */
function VehicleHeroCard({ car, records = [], currentMileage = 0 }) {
  const stats = useMemo(() => {
    const recordCount = records.length;
    const lastService = records.reduce((latest, record) => {
      if (!latest || Number(record.mileage) > Number(latest.mileage)) return record;
      return latest;
    }, null);
    const costRecords = records.filter((record) => record.cost != null);
    const totalSpend = costRecords.reduce((sum, record) => sum + Number(record.cost || 0), 0);
    return { recordCount, lastService, totalSpend, hasCostData: costRecords.length > 0 };
  }, [records]);

  if (!car) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <DirectionsCarFilledRoundedIcon sx={{ fontSize: 30, color: "text.secondary" }} />
        <Box>
          <Typography sx={{ fontWeight: 700 }}>No car selected</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Add a car to get started tracking maintenance.
          </Typography>
        </Box>
      </Box>
    );
  }

  const title = car.nickname
    ? `${car.nickname} (${car.year} ${car.make} ${car.model})`
    : `${car.year} ${car.make} ${car.model}`;

  const lastServiceLabel = stats.lastService
    ? stats.lastService.service_type || stats.lastService.serviceType || "Service"
    : "—";
  const lastServiceSublabel = stats.lastService ? stats.lastService.date : "No services logged yet";

  const statItems = [
    { label: "Current Mileage", value: `${currentMileage.toLocaleString()} mi` },
    { label: "Records Logged", value: stats.recordCount, mono: false },
    { label: "Last Service", value: lastServiceLabel, sublabel: lastServiceSublabel, mono: false },
    { label: "Total Spend", value: stats.hasCostData ? `$${stats.totalSpend.toFixed(2)}` : "No cost data" },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            display: "grid",
            placeItems: "center",
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.14),
            flexShrink: 0,
          }}
        >
          <DirectionsCarFilledRoundedIcon sx={{ color: "primary.main" }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
      </Box>

      {/* Grid, not flex-wrap: a wrap-based layout combined with "not the
          first item" left-dividers misplaces the divider/indent once items
          wrap to a second line on narrow screens. Grid sidesteps that --
          dividers only apply at sm+ where a single row is guaranteed. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
          rowGap: 2,
          columnGap: { xs: 2, sm: 0 },
        }}
      >
        {statItems.map((item, i) => (
          <Box
            key={item.label}
            sx={{
              pl: { sm: i === 0 ? 0 : 2.5 },
              ml: { sm: i === 0 ? 0 : 2.5 },
              borderLeft: { sm: i === 0 ? "none" : "1px solid" },
              borderColor: "divider",
              minWidth: 0,
            }}
          >
            <StatTile {...item} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default VehicleHeroCard;
